import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { listAlliances } from '../../common/allianceApi'
import {
  getAllCastles,
  getUserCastle,
  getWorldConfig,
  peekCachedWorldConfig,
} from '../../game/world-map/worldMapApi'
import { warmWorldMapConfig } from '../../game/world-map/worldMapPreload'
import type { CastleData } from '../../game/world-map/terrial-engine/types'
import {
  ESPIONAGE_V2_IMAGE_SRC,
  ESPIONAGE_V2_WORLD_MAP,
  ESPIONAGE_V2_WORLD_MAP_INSPECT_ICON,
  ESPIONAGE_V2_WORLD_MAP_KNIFE,
  ESPIONAGE_V2_WORLD_MAP_SCROLL,
  layoutRectToStyle,
} from './espionagePageV2Layout'
import { ESPIONAGE_MISSION_ICON_SEARCH } from './espionageAssets'
import {
  drawEspionageCastleFlagScreen,
  preloadEspionageAllianceFlags,
  subscribeEspionageAllianceFlagLoads,
} from './espionageV2FlagDraw'
import {
  drawEspionageV2WorldMapBackground,
  preloadEspionageV2WorldMapBackground,
  subscribeEspionageV2WorldMapBackgroundLoad,
} from './espionageV2WorldMapBackground'
import { drawEspionageV2MapGrid, drawEspionageV2MapSurface } from './espionageV2WorldMapPaint'
import {
  buildAllianceLeaderWalletSet,
  buildAllianceMarkerColorMap,
  castleToEspionageMapPoint,
  centerEspionageMapOnPoint,
  clampEspionageMapCameraToCluster,
  computeCastleClusterMapBounds,
  computeEspionageV2MapTransform,
  createEspionageV2MapCamera,
  espionageClientToCanvasPoint,
  espionageMapToScreenPoint,
  getAllianceLabelColors,
  getEspionageMapCanvasVisualScale,
  getEspionageMapLabelMetrics,
  getEspionageMapMarkerMetrics,
  getMarkerColorForCastle,
  getTransformContentBounds,
  hasCastleHex,
  isLegionCommanderCastle,
  normalizeCastleHex,
  zoomEspionageMapAtScreen,
  type EspionageV2MapCamera,
  type EspionageV2MapContentBounds,
  type EspionageV2MapTransform,
} from './espionageV2WorldMapUtils'

interface EspionageV2WorldMapProps {
  walletAddress?: string | null
  selectedCastleId?: string | null
  onSelectCastle?: (castle: CastleData) => void
  onInspectCastle?: (castle: CastleData) => void
  /** Scale-to-fit factor from {@link EspionagePageV2} — fixes touch pan on scaled mobile layout. */
  pageScale?: number
  /** Hide the decorative knife/scroll overlays (used when embedding the bare map in a modal). */
  hideDecor?: boolean
  /** Only render each alliance's LEADER castle (one per alliance) — used by the diplomacy picker. */
  leadersOnly?: boolean
}

type MapLoadPhase = 'loading' | 'ready' | 'error'

const CLICK_DRAG_THRESHOLD_PX = 6
const SCREEN_CULL_MARGIN = 56
const LOAD_TIMEOUT_MS = 25_000
const DEFAULT_WORLD_ID = 'main'
const ALLIANCE_FLAG_ASPECT = 0.72

function computeCastleInspectIconPosition(
  screenX: number,
  screenY: number,
  zoom: number,
  hasAllianceFlag: boolean,
): { x: number; y: number } {
  const m = getEspionageMapMarkerMetrics(zoom)
  const { size, offsetAboveFlag } = ESPIONAGE_V2_WORLD_MAP_INSPECT_ICON
  const flagHeight = hasAllianceFlag
    ? Math.max(8, Math.round(m.flagWidth * ALLIANCE_FLAG_ASPECT))
    : 0
  const anchorTop = hasAllianceFlag
    ? screenY - flagHeight - m.flagGap
    : screenY - m.pinAnchorLift - m.pinRadius
  return {
    x: screenX,
    y: anchorTop - offsetAboveFlag - size / 2,
  }
}

function isCastleInspectIconInMapBounds(
  pos: { x: number; y: number },
  mapViewport: { width: number; height: number },
): boolean {
  const { size } = ESPIONAGE_V2_WORLD_MAP_INSPECT_ICON
  const half = size / 2
  return (
    pos.x - half >= 0 &&
    pos.x + half <= mapViewport.width &&
    pos.y - half >= 0 &&
    pos.y + half <= mapViewport.height
  )
}

async function resolveWorldId(): Promise<string> {
  const cached = peekCachedWorldConfig()
  if (cached?.worldId) return cached.worldId
  try {
    const cfg = await getWorldConfig()
    if (cfg?.worldId) return cfg.worldId
  } catch (err) {
    console.warn('[EspionageV2WorldMap] getWorldConfig failed, using default world:', err)
  }
  return DEFAULT_WORLD_ID
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms}ms`))
    }, ms)
    promise
      .then((value) => {
        window.clearTimeout(timer)
        resolve(value)
      })
      .catch((err) => {
        window.clearTimeout(timer)
        reject(err)
      })
  })
}

export const EspionageV2WorldMap: React.FC<EspionageV2WorldMapProps> = ({
  walletAddress,
  selectedCastleId,
  onSelectCastle,
  onInspectCastle,
  pageScale = 1,
  hideDecor = false,
  leadersOnly = false,
}) => {
  const { t } = useTranslation()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const castlesRef = useRef<CastleData[]>([])
  const transformRef = useRef<EspionageV2MapTransform | null>(null)
  const clusterBoundsRef = useRef<EspionageV2MapContentBounds | null>(null)
  const cameraRef = useRef<EspionageV2MapCamera>(createEspionageV2MapCamera())
  const leaderMapRef = useRef<Map<string, string>>(new Map())
  const allianceColorsRef = useRef<Map<string, number>>(new Map())
  const dragRef = useRef<{
    active: boolean
    moved: boolean
    startClientX: number
    startClientY: number
    startPanX: number
    startPanY: number
    visualScale: number
  } | null>(null)
  const pageScaleRef = useRef(pageScale)
  const worldSizeRef = useRef<{ width: number; height: number } | null>(null)

  const [loadPhase, setLoadPhase] = useState<MapLoadPhase>('loading')
  const [loadMessage, setLoadMessage] = useState('')
  const [castles, setCastles] = useState<CastleData[]>([])
  const [transform, setTransform] = useState<EspionageV2MapTransform | null>(null)
  const [leaderByAllianceId, setLeaderByAllianceId] = useState<Map<string, string>>(new Map())
  const [allianceColors, setAllianceColors] = useState<Map<string, number>>(new Map())
  const [isDragging, setIsDragging] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [inspectIconPos, setInspectIconPos] = useState<{ x: number; y: number } | null>(null)
  const selectedCastleRef = useRef<CastleData | null>(null)

  const viewport = useMemo(
    () => ({ width: ESPIONAGE_V2_WORLD_MAP.width, height: ESPIONAGE_V2_WORLD_MAP.height }),
    [],
  )

  const clampCameraToCluster = useCallback(() => {
    const content = clusterBoundsRef.current
    if (!content) return
    clampEspionageMapCameraToCluster(cameraRef.current, viewport, content)
  }, [viewport])

  const updateInspectIconPos = useCallback(() => {
    if (!selectedCastleId || !transformRef.current) {
      setInspectIconPos(null)
      selectedCastleRef.current = null
      return
    }
    const castle = castlesRef.current.find((c) => String(c._id) === String(selectedCastleId))
    if (!castle) {
      setInspectIconPos(null)
      selectedCastleRef.current = null
      return
    }
    selectedCastleRef.current = castle
    const t = transformRef.current
    const camera = cameraRef.current
    const { x, y } = castleToEspionageMapPoint(castle, t)
    const screen = espionageMapToScreenPoint(x, y, viewport, camera)
    const hasAllianceFlag = Boolean(castle.allianceFlagUrl?.trim())
    const pos = computeCastleInspectIconPosition(screen.x, screen.y, camera.zoom, hasAllianceFlag)
    setInspectIconPos(isCastleInspectIconInMapBounds(pos, viewport) ? pos : null)
  }, [selectedCastleId, viewport])

  const paintFrame = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const { width, height } = viewport
    ctx.clearRect(0, 0, width, height)

    const t = transformRef.current
    const list = castlesRef.current
    const leaders = leaderMapRef.current
    const allianceColorMap = allianceColorsRef.current
    const camera = cameraRef.current
    const worldSize = worldSizeRef.current

    if (t && worldSize) {
      drawEspionageV2WorldMapBackground(
        ctx,
        viewport,
        camera,
        t,
        worldSize.width,
        worldSize.height,
      )
    }

    drawEspionageV2MapSurface(ctx, viewport)

    if (t) {
      drawEspionageV2MapGrid(ctx, viewport, camera)
    }

    if (!t || list.length === 0) return

    const me = (walletAddress || '').toLowerCase().trim()
    const sorted = [...list].sort((a, b) => {
      const pa = castleToEspionageMapPoint(a, t)
      const pb = castleToEspionageMapPoint(b, t)
      return pa.y - pb.y
    })

    for (const castle of sorted) {
      const { x, y } = castleToEspionageMapPoint(castle, t)
      const screen = espionageMapToScreenPoint(x, y, viewport, camera)
      if (
        screen.x < -SCREEN_CULL_MARGIN ||
        screen.x > width + SCREEN_CULL_MARGIN ||
        screen.y < -SCREEN_CULL_MARGIN ||
        screen.y > height + SCREEN_CULL_MARGIN
      ) {
        continue
      }

      const isOwn = !!me && String(castle.ownerId || '').toLowerCase().trim() === me
      const isCommander = isLegionCommanderCastle(castle, leaders)
      const isSelected = selectedCastleId === castle._id
      const markerColor = getMarkerColorForCastle(castle, allianceColorMap)

      const markerMetrics = getEspionageMapMarkerMetrics(camera.zoom)

      drawEspionageCastleFlagScreen(ctx, castle, screen.x, screen.y, {
        isOwn,
        isSelected,
        markerColor,
        zoom: camera.zoom,
      })

      if (isCommander) {
        ctx.fillStyle = '#e8c04a'
        ctx.strokeStyle = '#3e2723'
        ctx.lineWidth = markerMetrics.strokeWidth
        const starY = screen.y - markerMetrics.commanderStarOffsetY
        ctx.beginPath()
        for (let i = 0; i < 5; i++) {
          const angle = (Math.PI * 2 * i) / 5 - Math.PI / 2
          const outer = markerMetrics.commanderStarOuter
          const inner = markerMetrics.commanderStarInner
          const ox = screen.x + Math.cos(angle) * outer
          const oy = starY + Math.sin(angle) * outer
          if (i === 0) ctx.moveTo(ox, oy)
          else ctx.lineTo(ox, oy)
          const angle2 = angle + Math.PI / 5
          ctx.lineTo(
            screen.x + Math.cos(angle2) * inner,
            starY + Math.sin(angle2) * inner,
          )
        }
        ctx.closePath()
        ctx.fill()
        ctx.stroke()
      }

      const lord = (castle.ownerName || '').trim()
      const legion = (castle.allianceName || '').trim()
      const labels = getEspionageMapLabelMetrics(camera.zoom)
      ctx.textAlign = 'center'
      ctx.textBaseline = 'top'

      if (lord) {
        ctx.font = `600 ${labels.lordFontSize}px "Cinzel", "Times New Roman", serif`
        ctx.fillStyle = 'rgba(12, 8, 5, 0.75)'
        ctx.fillText(
          lord,
          screen.x + labels.shadowOffset,
          screen.y + labels.labelTopOffset + labels.shadowOffset,
          labels.maxTextWidth,
        )
        ctx.fillStyle = '#e8dcc8'
        ctx.fillText(lord, screen.x, screen.y + labels.labelTopOffset, labels.maxTextWidth)
      }

      if (legion) {
        const legionY = lord
          ? screen.y + labels.labelTopOffset + labels.legionLineGap
          : screen.y + labels.labelTopOffset
        const allianceId = castle.allianceId ? String(castle.allianceId).trim() : ''
        const legionColors =
          allianceId && allianceColorMap.has(allianceId)
            ? getAllianceLabelColors(allianceColorMap.get(allianceId)!)
            : { fill: '#b8986a', shadow: 'rgba(12, 8, 5, 0.7)' }
        ctx.font = `500 ${labels.legionFontSize}px "Cinzel", "Times New Roman", serif`
        ctx.fillStyle = legionColors.shadow
        ctx.fillText(
          legion,
          screen.x + labels.shadowOffset,
          legionY + labels.shadowOffset,
          labels.maxTextWidth,
        )
        ctx.fillStyle = legionColors.fill
        ctx.fillText(legion, screen.x, legionY, labels.maxTextWidth)
      }
    }

    updateInspectIconPos()
  }, [selectedCastleId, updateInspectIconPos, viewport, walletAddress])

  // Sync refs before paint — useLayoutEffect runs before paint; a separate useEffect
  // would leave refs stale when paintFrame first runs after map data loads.
  useLayoutEffect(() => {
    castlesRef.current = castles
    transformRef.current = transform
    clusterBoundsRef.current = transform
      ? computeCastleClusterMapBounds(castles, transform) ?? getTransformContentBounds(transform)
      : null
    leaderMapRef.current = leaderByAllianceId
    allianceColorsRef.current = allianceColors
    paintFrame()
  }, [paintFrame, castles, transform, leaderByAllianceId, allianceColors, loadPhase, loadAttempt])

  useLayoutEffect(() => {
    updateInspectIconPos()
  }, [updateInspectIconPos, selectedCastleId, loadPhase])

  useEffect(() => {
    warmWorldMapConfig()
    preloadEspionageV2WorldMapBackground()
    const unsubFlags = subscribeEspionageAllianceFlagLoads(paintFrame)
    const unsubMapBg = subscribeEspionageV2WorldMapBackgroundLoad(paintFrame)
    return () => {
      unsubFlags()
      unsubMapBg()
    }
  }, [paintFrame])

  useEffect(() => {
    preloadEspionageAllianceFlags(castles)
  }, [castles])

  const loadMapData = useCallback(async () => {
    setLoadPhase('loading')
    setLoadMessage(t('espionage.worldMapLoading'))

    const worldId = await resolveWorldId()
    const cachedConfig = peekCachedWorldConfig()
    const [castleList, userCastle, allianceList, worldConfig] = await Promise.all([
      // Espionage stays region-scoped: spy missions only target the viewer's own
      // strategic region, so other regions' players are not shown here (the
      // regular world map, in contrast, shows every region).
      withTimeout(getAllCastles(worldId, { regionScope: true }), LOAD_TIMEOUT_MS, 'getAllCastles'),
      walletAddress
        ? withTimeout(getUserCastle(worldId), LOAD_TIMEOUT_MS, 'getUserCastle').catch(() => null)
        : Promise.resolve(null),
      walletAddress
        ? listAlliances(walletAddress).catch(() => ({ alliances: [] }))
        : Promise.resolve({ alliances: [] }),
      cachedConfig
        ? Promise.resolve(cachedConfig)
        : getWorldConfig().catch(() => null),
    ])

    if (worldConfig?.width && worldConfig?.height) {
      worldSizeRef.current = { width: worldConfig.width, height: worldConfig.height }
    }

    const byId = new Map<string, CastleData>()
    for (const raw of castleList) {
      if (!hasCastleHex(raw)) continue
      const c = normalizeCastleHex(raw)
      byId.set(String(c._id), c)
    }
    if (userCastle && hasCastleHex(userCastle)) {
      const c = normalizeCastleHex(userCastle)
      byId.set(String(c._id), c)
    }
    const leaders = buildAllianceLeaderWalletSet(allianceList.alliances || [])
    const placedAll = [...byId.values()]
    // Diplomacy picker: show only each alliance's leader castle (one selectable castle per alliance).
    const placed = leadersOnly ? placedAll.filter((c) => isLegionCommanderCastle(c, leaders)) : placedAll
    const nextTransform = computeEspionageV2MapTransform(placed, viewport)

    cameraRef.current = createEspionageV2MapCamera()
    clusterBoundsRef.current = nextTransform
      ? computeCastleClusterMapBounds(placed, nextTransform) ??
        getTransformContentBounds(nextTransform)
      : null
    if (clusterBoundsRef.current) {
      clampEspionageMapCameraToCluster(cameraRef.current, viewport, clusterBoundsRef.current)
    }
    if (nextTransform && userCastle && hasCastleHex(userCastle)) {
      const { x, y } = castleToEspionageMapPoint(normalizeCastleHex(userCastle), nextTransform)
      centerEspionageMapOnPoint(x, y, viewport, cameraRef.current)
      if (clusterBoundsRef.current) {
        clampEspionageMapCameraToCluster(
          cameraRef.current,
          viewport,
          clusterBoundsRef.current,
        )
      }
    }

    return { placed, nextTransform, leaders }
  }, [viewport, walletAddress, t, leadersOnly])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { placed, nextTransform, leaders } = await loadMapData()
        if (cancelled) return

        setCastles(placed)
        setTransform(nextTransform)
        setLeaderByAllianceId(leaders)
        setAllianceColors(buildAllianceMarkerColorMap(placed))

        if (!nextTransform) {
          setLoadPhase('error')
          setLoadMessage(t('espionage.worldMapFitError'))
          return
        }
        if (placed.length === 0) {
          setLoadPhase('ready')
          setLoadMessage(t('espionage.worldMapNoCastles'))
          return
        }
        setLoadPhase('ready')
        setLoadMessage('')
      } catch (err) {
        console.error('[EspionageV2WorldMap] load failed:', err)
        if (!cancelled) {
          setCastles([])
          setTransform(null)
          setAllianceColors(new Map())
          setLoadPhase('error')
          setLoadMessage(
            err instanceof Error ? err.message : t('espionage.worldMapLoadFailed'),
          )
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [loadMapData, loadAttempt, t])

  const resetCamera = useCallback(() => {
    cameraRef.current = createEspionageV2MapCamera()
    clampCameraToCluster()
    if (transformRef.current && castlesRef.current.length > 0) {
      const me = (walletAddress || '').toLowerCase().trim()
      const own = castlesRef.current.find(
        (c) => me && String(c.ownerId || '').toLowerCase().trim() === me,
      )
      if (own && transformRef.current) {
        const { x, y } = castleToEspionageMapPoint(own, transformRef.current)
        centerEspionageMapOnPoint(x, y, viewport, cameraRef.current)
        clampCameraToCluster()
      }
    }
    paintFrame()
  }, [clampCameraToCluster, paintFrame, viewport, walletAddress])

  useEffect(() => {
    pageScaleRef.current = pageScale
  }, [pageScale])

  const getVisualScale = useCallback(
    (canvas: HTMLCanvasElement) =>
      getEspionageMapCanvasVisualScale(canvas, viewport, pageScaleRef.current),
    [viewport],
  )

  const clientToCanvas = useCallback(
    (
      canvas: HTMLCanvasElement,
      clientX: number,
      clientY: number,
      visualScale?: number,
    ) => {
      const scale = visualScale ?? getVisualScale(canvas)
      return espionageClientToCanvasPoint(canvas, clientX, clientY, scale)
    },
    [getVisualScale],
  )

  const applyDragPan = useCallback(
    (clientX: number, clientY: number) => {
      const drag = dragRef.current
      if (!drag?.active) return
      const dx = (clientX - drag.startClientX) / drag.visualScale
      const dy = (clientY - drag.startClientY) / drag.visualScale
      if (!drag.moved && Math.hypot(dx, dy) > CLICK_DRAG_THRESHOLD_PX) {
        drag.moved = true
      }
      cameraRef.current.panX = drag.startPanX + dx
      cameraRef.current.panY = drag.startPanY + dy
      clampCameraToCluster()
      paintFrame()
    },
    [clampCameraToCluster, paintFrame],
  )

  const handleWheel = useCallback(
    (event: React.WheelEvent<HTMLCanvasElement>) => {
      if (loadPhase !== 'ready' || !transform) return
      event.preventDefault()
      const pt = clientToCanvas(event.currentTarget, event.clientX, event.clientY)
      const factor = event.deltaY < 0 ? 1.12 : 1 / 1.12
      zoomEspionageMapAtScreen(cameraRef.current, viewport, pt.x, pt.y, factor)
      clampCameraToCluster()
      paintFrame()
    },
    [clampCameraToCluster, clientToCanvas, loadPhase, paintFrame, transform, viewport],
  )

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (loadPhase !== 'ready' || !transform) return
      const canvas = event.currentTarget
      const visualScale = getVisualScale(canvas)
      dragRef.current = {
        active: true,
        moved: false,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startPanX: cameraRef.current.panX,
        startPanY: cameraRef.current.panY,
        visualScale,
      }
      setIsDragging(true)
      canvas.setPointerCapture(event.pointerId)
      if (event.pointerType === 'touch') {
        event.preventDefault()
      }
    },
    [getVisualScale, loadPhase, transform],
  )

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      const drag = dragRef.current
      if (!drag?.active) return
      if (event.pointerType === 'touch') {
        event.preventDefault()
      }
      applyDragPan(event.clientX, event.clientY)
    },
    [applyDragPan],
  )

  const endPointer = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current
    if (!drag?.active) return
    dragRef.current = null
    setIsDragging(false)
    try {
      event.currentTarget.releasePointerCapture(event.pointerId)
    } catch {
      /* ignore */
    }
  }, [])

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      const drag = dragRef.current
      if (!drag?.active) return

      if (!drag.moved) {
        const t = transformRef.current
        const list = castlesRef.current
        const camera = cameraRef.current
        if (t && list.length > 0) {
          const pt = clientToCanvas(event.currentTarget, event.clientX, event.clientY)
          const hitRadius = getEspionageMapMarkerMetrics(camera.zoom).hitRadiusPx
          let best: CastleData | null = null
          let bestDist = Infinity
          for (const castle of list) {
            const { x, y } = castleToEspionageMapPoint(castle, t)
            const screen = espionageMapToScreenPoint(x, y, viewport, camera)
            const d = (screen.x - pt.x) ** 2 + (screen.y - pt.y) ** 2
            if (d < hitRadius ** 2 && d < bestDist) {
              bestDist = d
              best = castle
            }
          }
          if (best) onSelectCastle?.(best)
        }
      }

      endPointer(event)
    },
    [clientToCanvas, endPointer, onSelectCastle, viewport],
  )

  const handleDoubleClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      event.preventDefault()
      resetCamera()
    },
    [resetCamera],
  )

  useEffect(() => {
    if (!isDragging) return

    const onWindowPointerMove = (event: PointerEvent) => {
      const drag = dragRef.current
      if (!drag?.active) return
      if (event.pointerType === 'touch') {
        event.preventDefault()
      }
      applyDragPan(event.clientX, event.clientY)
    }

    window.addEventListener('pointermove', onWindowPointerMove, { passive: false })
    return () => window.removeEventListener('pointermove', onWindowPointerMove)
  }, [applyDragPan, isDragging])

  const showStatusOverlay = loadPhase !== 'ready' || castles.length === 0

  return (
    <div
      className="absolute overflow-visible pointer-events-none"
      style={{
        left: ESPIONAGE_V2_WORLD_MAP.x,
        top: ESPIONAGE_V2_WORLD_MAP.y,
        width: ESPIONAGE_V2_WORLD_MAP.width,
        height: ESPIONAGE_V2_WORLD_MAP.height,
        zIndex: 30,
      }}
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-auto">
      <canvas
        ref={canvasRef}
        width={viewport.width}
        height={viewport.height}
        className="relative z-[1] block w-full h-full touch-none select-none"
        style={{
          touchAction: 'none',
          cursor:
            loadPhase !== 'ready' || !transform
              ? 'default'
              : isDragging
                ? 'grabbing'
                : 'grab',
        }}
        onWheel={handleWheel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={endPointer}
        onDoubleClick={handleDoubleClick}
        aria-label="Espionage world map"
      />
      </div>

      {!hideDecor ? (
        <>
          <img
            src={ESPIONAGE_V2_IMAGE_SRC.imgKnife}
            alt=""
            draggable={false}
            className="absolute object-fill pointer-events-none z-[3]"
            style={layoutRectToStyle(ESPIONAGE_V2_WORLD_MAP_KNIFE)}
          />
          <img
            src={ESPIONAGE_V2_IMAGE_SRC.imgScroll}
            alt=""
            draggable={false}
            className="absolute object-fill pointer-events-none z-[3]"
            style={layoutRectToStyle(ESPIONAGE_V2_WORLD_MAP_SCROLL)}
          />
        </>
      ) : null}

      {inspectIconPos && selectedCastleRef.current && onInspectCastle ? (
        <button
          type="button"
          className="absolute z-[5] flex items-center justify-center rounded-full border border-amber-700/70 bg-[#2a1f14]/90 shadow-[0_2px_8px_rgba(0,0,0,0.65)] hover:brightness-110 active:scale-95 transition-[filter,transform] pointer-events-auto"
          style={{
            left: inspectIconPos.x,
            top: inspectIconPos.y,
            width: ESPIONAGE_V2_WORLD_MAP_INSPECT_ICON.size,
            height: ESPIONAGE_V2_WORLD_MAP_INSPECT_ICON.size,
            transform: 'translate(-50%, -50%)',
          }}
          aria-label={t('espionage.inspectCastle')}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation()
            const castle = selectedCastleRef.current
            if (castle) onInspectCastle(castle)
          }}
        >
          <img
            src={ESPIONAGE_MISSION_ICON_SEARCH}
            alt=""
            draggable={false}
            className="w-[70%] h-[70%] object-contain pointer-events-none"
          />
        </button>
      ) : null}

      {showStatusOverlay ? (
        <div
          className="absolute inset-0 z-[4] flex flex-col items-center justify-center gap-2 px-4 text-center pointer-events-none"
          style={{
            background:
              loadPhase === 'loading'
                ? 'rgba(12, 8, 5, 0.35)'
                : 'rgba(12, 8, 5, 0.2)',
          }}
        >
          {loadPhase === 'loading' ? (
            <div
              className="h-7 w-7 rounded-full border-2 border-[#b8986a]/30 border-t-[#e8c878] animate-spin"
              aria-hidden
            />
          ) : null}
          {loadMessage ? (
            <p
              style={{
                fontFamily: '"Cinzel", serif',
                fontSize: 12,
                fontWeight: 600,
                color: loadPhase === 'error' ? '#c97a5a' : '#e8dcc8',
                textShadow: '0 1px 3px rgba(0,0,0,0.8)',
              }}
            >
              {loadMessage}
            </p>
          ) : null}
          {loadPhase === 'error' ? (
            <button
              type="button"
              className="pointer-events-auto mt-1 px-3 py-1 rounded border border-amber-900/60 text-[11px] font-semibold text-[#e8c878] hover:brightness-110"
              style={{ fontFamily: '"Cinzel", serif' }}
              onClick={() => setLoadAttempt((n) => n + 1)}
            >
              {t('espionage.retry')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default EspionageV2WorldMap
